// external
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Packer } from "docx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

// state management
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { FaoResultsContext } from "context/FaoResultsContext";

// helpers
import { createDocument } from "../fao-export-model";
import { handleUnpublishModel } from "../helpers/published-model-helpers";
import apiRoot from "api/apiRoot";
import { ClientContext } from "context/ClientContext";

/**
 * useViewResults Hook
 *
 * This hook provides action handlers for the FAO view results page.
 * It manages the primary actions users can take when viewing published model results:
 *
 * Key Responsibilities:
 * - Handles model unpublishing with proper loading states
 * - Manages export functionality (placeholder for future implementation)
 * - Provides loading state management for UI feedback
 * - Integrates with navigation for post-action routing
 *
 * The hook abstracts the business logic for view results actions,
 * keeping the component focused on rendering and user interaction.
 */
function useViewResults() {
  const navigate = useNavigate();
  const { modelConfig } = useContext(FaoBuilderContext);
  const {
    setLoading,
    costOfAttendance,
    firstYearEstimate,
    nonModeledData,
    isSaved,
    setIsExporting,
  } = useContext(FaoResultsContext);
  const { currentClientData } = useContext(ClientContext);
  /**
   * Handles the unpublish model action using the unified handler
   */
  const handleUnpublishModelHandler = () => {
    handleUnpublishModel(modelConfig, navigate, setLoading);
  };

  /**
   * Handles the export model action
   *
   * Note: This is currently a placeholder for future export functionality.
   * When implemented, this should:
   * - Trigger model data export (PDF, Excel, etc.)
   * - Show appropriate loading states
   * - Handle download initiation
   * - Provide user feedback on success/failure
   */
  const getRegimes = async () => {
    try {
      const existingRegimeId = modelConfig.configs["regimeParams"];
      const response = await apiRoot.get(
        `/api/fao-regime/regimeparameters/${existingRegimeId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error in getRegimes:", error);
      return {};
    }
  };

  const getSpecialFunds = async () => {
    try {
      const existingAwardingsId = modelConfig.configs["awardingStrategy"];
      const response = await apiRoot.get(
        `/api/fao-awardings/${existingAwardingsId}`
      );
      return response.data.specialFundAdjustments;
    } catch (error) {
      console.error("Error in getSpecialFunds:", error);
      return [];
    }
  };
  const handleExportModel = async () => {
    console.log(currentClientData);
    if (!Object.values(isSaved).some((item) => Boolean(item))) {
      toast.error("Please save adjustments before exporting");
      return;
    }
    try {
      setIsExporting(true);
      const regimes = await getRegimes();
      const xdimensions = regimes?.xDimensions;
      const ydimensions = regimes?.yDimensions;
      const matrices = regimes?.matrices;

      const specialFunds = await getSpecialFunds();

      const doc = await createDocument({
        costOfAttendance: costOfAttendance,
        xdimensions: xdimensions,
        ydimensions: ydimensions,
        matrices: matrices,
        firstYearEstimate: firstYearEstimate[0],
        modelResults: modelConfig.aidResults,
        nonModeledData: nonModeledData.adjusted,
        specialFunds: specialFunds,
      });

      const blob = await Packer.toBlob(doc);
      const docName = `${currentClientData.blobContainer}_${currentClientData.schoolKey}_model#${modelConfig.versionName}_Packaging_Policy.docx`;
      saveAs(blob, docName);
    } catch (error) {
      toast.error("Error exporting model");
      console.error("Error in handleExportModel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Return action handlers for use in components
  return {
    handleUnpublishModelHandler,
    handleExportModel,
  };
}

export default useViewResults;
